import {
  Job,
  Property,
  PropertyGroup,
  PropertyGroupListItem,
  Ruleset,
  User,
} from 'types';
import { getRequestHeaders, handleRes, logError } from 'utils';

export interface GetPropertiesResponse extends Response {
  property?: Array<Property>;
}
export interface CreatePropertyGroupResponse extends Response {
  ID?: object;
}

export interface GetPropertyGroupJobsResponse extends Response {
  jobs?: Job[];
}

interface CreatePropertyGroupParams {
  body: string; // PropertyGroup JSON string
  tenantID: string | number | undefined;
}
export const createPropertyGroup = async ({
  body,
  tenantID,
}: CreatePropertyGroupParams): Promise<CreatePropertyGroupResponse> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups`,
    {
      method: 'POST',
      ...getRequestHeaders(),
      body,
    },
  );
};

interface UpdatePropertyGroupParams {
  tenantID: string | number | undefined;
  groupID: string | number | undefined;
  body: string; // PropertyGroup JSON string
}
export const updatePropertyGroup = async ({
  tenantID,
  groupID,
  body,
}: UpdatePropertyGroupParams): Promise<CreatePropertyGroupResponse> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups/${groupID}`,
    {
      method: 'PUT',
      ...getRequestHeaders(),
      body,
    },
  );
};

interface GetPropertyGroupUsers {
  tenantID: number | string | undefined;
  groupID: number | string | undefined;
}
export const getPropertyGroupUsers = ({
  tenantID,
  groupID,
}: GetPropertyGroupUsers): Promise<User[]> => {
  return fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups/${groupID}/users`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<User[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};

interface GetPropertiesByPropertyGroupIDParams {
  groupID: number | string | undefined;
  tenantID: number | string | undefined;
}
export const getPropertiesByPropertyGroupID = ({
  groupID,
  tenantID,
}: GetPropertiesByPropertyGroupIDParams): Promise<Property[]> => {
  return fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups/${groupID}/properties`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Property[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};

export const getTenantPropertyGroupsList = (
  tenantID: number | string | undefined,
  filterAccessError: boolean = false,
): Promise<PropertyGroupListItem[]> => {
  return fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups/list`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<PropertyGroupListItem[]>(res, 200))
    .catch((e: Error) => {
      if (filterAccessError && e.message.includes('permissions')) {
        return [];
      } else {
        logError(e);
        return [];
      }
    });
};

export const getTenantPropertyGroups = (
  tenantID: number | string | undefined,
  filterAccessError: boolean = false,
): Promise<PropertyGroup[]> => {
  return fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<PropertyGroup[]>(res, 200))
    .catch((e: Error) => {
      // I don't need to know about dashboard access 'errors'
      if (filterAccessError && e.message.includes('permissions')) {
        return [];
      } else {
        logError(e);
        return [];
      }
    });
};

export interface GetPropertyGroupByIDParams {
  tenantID: string | number | undefined;
  groupID: string | number | undefined;
}
export const getPropertyGroupByID = ({
  tenantID,
  groupID,
}: GetPropertyGroupByIDParams): Promise<PropertyGroup> => {
  return fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups/${groupID}`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<PropertyGroup>(res, 200))
    .catch((e) => {
      throw e;
    });
};

export interface PatchPropertyGroupParams {
  tenantID: string | number | undefined;
  groupID: string | number | undefined;
  body: string; // PropertyGroupUpdateBody
}
export const patchPropertyGroup = async ({
  tenantID,
  groupID,
  body,
}: PatchPropertyGroupParams): Promise<Response> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups/${groupID}`,
    {
      method: 'PATCH',
      ...getRequestHeaders(),
      body,
    },
  );
};

interface GetPropertyGroupRulesetsParams {
  tenantID: string | number | undefined;
  groupID: string | number | undefined;
}
export const getPropertyGroupRulesets = async ({
  tenantID,
  groupID,
}: GetPropertyGroupRulesetsParams): Promise<Ruleset[]> => {
  return fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/propertyGroups/${groupID}/rulesets`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Ruleset[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};
